import { useMemo, useCallback, FC, useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { AnimatePresence } from 'motion/react';
import { getUser, localStorageHelper } from '@frontend/auth-helpers';
import { WidgetPromo } from '@frontend/grid-dashboard';
import { useWidgetConfig } from './store';
import { useAccessibleWidgetConfig } from './use-accesible-widgets-config';
import { useWidgets } from './use-dashboard-widgets';
import { useWidgetPromo } from './use-widget-promo';
import { widgetLibraryImages } from './widget-library-images-map';

const DISMISSED_STORAGE_KEY = 'widget-promo-dismissed-list';
const ADDED_WIDGETS_STORAGE_KEY = 'widget-promo-added';

const createUserStorageKey = (baseKey: string, userId: string) => `${baseKey}-${userId}`;

const getDismissedWidgetIds = (userId: string): string[] => {
  const key = createUserStorageKey(DISMISSED_STORAGE_KEY, userId);
  const result = localStorageHelper.get(key);

  if (!result) {
    return [];
  }

  return Array.isArray(result) ? (result as string[]) : [];
};

const isWidgetPromoDismissed = (userId: string, currentWidgetIds: string[]): boolean => {
  const dismissedWidgetIds = getDismissedWidgetIds(userId);
  return currentWidgetIds.length > 0 && currentWidgetIds.every((id) => dismissedWidgetIds.includes(id));
};

const dismissPromoWidget = (userId: string, currentWidgetIds: string[]): void => {
  const key = createUserStorageKey(DISMISSED_STORAGE_KEY, userId);
  const existingDismissedIds = getDismissedWidgetIds(userId);
  const updatedDismissedIds = [...new Set([...existingDismissedIds, ...currentWidgetIds])];
  localStorageHelper.create(key, updatedDismissedIds);
};

const getAddedWidgets = (userId: string): Record<string, boolean> => {
  const key = createUserStorageKey(ADDED_WIDGETS_STORAGE_KEY, userId);
  return (localStorageHelper.get(key) as Record<string, boolean>) || {};
};

const addWidgetToStorage = (widgetId: string, userId: string): void => {
  const key = createUserStorageKey(ADDED_WIDGETS_STORAGE_KEY, userId);
  const addedWidgets = getAddedWidgets(userId);
  addedWidgets[widgetId] = true;
  localStorageHelper.create(key, addedWidgets);
};

export const useWidgetPromoLogic = () => {
  const widgetPromo = useWidgetPromo();
  const { widgets } = useWidgets();
  const userId = getUser()?.userID;

  const { accessibleWidgets: widgetConfigs, safeAddWidget: addWidget } = useAccessibleWidgetConfig();
  const [originalPromoWidgets, setOriginalPromoWidgets] = useState<string[]>([]);
  const [isPromoActive, setIsPromoActive] = useState(false);

  const widgetPromoKeys = useMemo(() => {
    return Object.keys(widgetPromo) as (keyof typeof widgetPromo)[];
  }, [widgetPromo]);

  const mappedWidgets = useMemo(() => {
    return widgetPromoKeys.map((key) => {
      const size = widgetPromo?.[key]?.promoImage;
      const widgetLibraryImage = widgetLibraryImages[key as keyof typeof widgetLibraryImages];
      const libraryImage = widgetLibraryImage?.[size as keyof typeof widgetLibraryImage];

      return {
        id: key,
        description: widgets[key]?.description || '',
        title: widgets[key]?.title || '',
        libraryImage: libraryImage || null,
        show: (widgetPromo?.[key] as any)?.show ?? widgets[key]?.hasAccess ?? true,
        currentSize: size,
        isEnabled: widgetConfigs.some((config) => config.id === key),
      };
    });
  }, [widgetPromo, widgets, widgetPromoKeys, widgetConfigs]);

  const widgetsToAdd = useMemo(() => {
    const now = dayjs();
    const previouslyAddedWidgets = userId ? getAddedWidgets(userId) : {};
    const dismissedWidgetIds = userId ? getDismissedWidgetIds(userId) : [];

    return mappedWidgets.filter((widget) => {
      const shouldShow = widget.show && !widget.isEnabled;
      if (!shouldShow) {
        return false;
      }

      if (previouslyAddedWidgets[widget.id]) {
        return false;
      }

      if (dismissedWidgetIds.includes(widget.id)) {
        return false;
      }

      const promoEndDate = widgetPromo[widget.id as keyof typeof widgetPromo]?.promoEndDate;
      if (promoEndDate) {
        return now.isBefore(dayjs(promoEndDate));
      }
      return true;
    });
  }, [mappedWidgets, widgetPromo, userId]);

  useEffect(() => {
    if (widgetsToAdd.length > 0 && !isPromoActive && userId) {
      const currentWidgetIds = widgetsToAdd.map((w) => w.id);

      if (!isWidgetPromoDismissed(userId, currentWidgetIds)) {
        setOriginalPromoWidgets(currentWidgetIds);
        setIsPromoActive(true);
      }
    }
  }, [widgetsToAdd, isPromoActive, userId]);

  const widgetsToShow = useMemo(() => {
    if (!isPromoActive || originalPromoWidgets.length === 0) {
      return [];
    }

    return mappedWidgets.filter((widget) => originalPromoWidgets.includes(widget.id) && widget.show);
  }, [isPromoActive, originalPromoWidgets, mappedWidgets]);

  const shouldAutoDismiss = useMemo(() => {
    if (!isPromoActive || originalPromoWidgets.length === 0) return false;

    return originalPromoWidgets.every((widgetId) => widgetConfigs.some((config) => config.id === widgetId));
  }, [isPromoActive, originalPromoWidgets, widgetConfigs]);

  useEffect(() => {
    if (shouldAutoDismiss && userId && originalPromoWidgets.length > 0) {
      dismissPromoWidget(userId, originalPromoWidgets);
      setIsPromoActive(false);
    }
  }, [shouldAutoDismiss, userId, originalPromoWidgets]);

  const isDismissed = useMemo(() => {
    return !isPromoActive || widgetsToShow.length === 0;
  }, [isPromoActive, widgetsToShow]);

  const handleDismiss = useCallback(() => {
    if (userId && originalPromoWidgets.length > 0) {
      dismissPromoWidget(userId, originalPromoWidgets);
      setIsPromoActive(false);
    }
  }, [userId, originalPromoWidgets]);

  const handleAddWidget = useCallback(
    (widgetId: string) => {
      if (userId) {
        addWidgetToStorage(widgetId, userId);

        setTimeout(() => {
          document.querySelector(`#dashboard-widget-${widgetId}`)?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center',
          });
        }, 100);
      }

      const targetWidget = widgetsToShow.find((widget) => widget.id === widgetId);
      addWidget({
        id: widgetId,
        size: targetWidget?.currentSize || 'medium-wide',
      });
    },
    [userId, widgetsToShow, addWidget]
  );

  return {
    widgetsToAdd: widgetsToShow,
    isDismissed,
    handleDismiss,
    handleAddWidget,
  };
};

interface WidgetPromoProps {
  innerRef?: React.LegacyRef<HTMLDivElement>; // This is required for priority renderer mount check
  onDismiss?: () => void;
}
export const WidgetPromotion: FC<WidgetPromoProps> = ({ innerRef, onDismiss }) => {
  const { widgetsToAdd, isDismissed, handleDismiss, handleAddWidget } = useWidgetPromoLogic();
  const { hasEditedWidgetConfigs } = useWidgetConfig(['hasEditedWidgetConfigs']);
  const hasWidgetConfigs = useWidgetConfig((state) => state.widgetConfigs.length > 0);

  if (hasEditedWidgetConfigs === false || !hasWidgetConfigs) {
    return null;
  }

  return isDismissed || widgetsToAdd.length === 0 ? null : (
    <AnimatePresence>
      <WidgetPromo
        innerRef={innerRef}
        onDismiss={() => {
          handleDismiss();
          onDismiss?.();
        }}
        onAddWidget={handleAddWidget}
        widgets={widgetsToAdd}
      />
    </AnimatePresence>
  );
};
